<script setup lang="ts">
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { type FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import { type SUREbusinessManagementSearchSearchType as ResultType } from '@services/overig/orval-api/model/sUREbusinessManagementSearchSearchType';
import { useSearchGet } from '@services/overig/orval-api/search/search';
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import VPopover from '@shared/component/VPopover/VPopover.vue';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VStatusTag, { type VStatusTagProps } from '@shared/component/VStatusTag/VStatusTag.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { groupBy } from '@tools/helpers';
import { useEventListener, useMagicKeys, useSessionStorage, whenever } from '@vueuse/core';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';

const term = ref<string>();
const route = useRoute();
const params = computed(() => ({ term: term.value, limit: 5 }));
const popoverRef = useTemplateRef('popover');
const isVisible = computed(() => popoverRef.value?.visible);
const iframeClicked = ref(false);

defineExpose({
  visible: isVisible,
});

const closePopover = () => {
  if (isVisible.value) popoverRef.value?.hide();
};

useEventListener('resize', closePopover);

useEventListener(window, 'blur', () => {
  setTimeout(() => {
    if (document.activeElement?.tagName === 'IFRAME') {
      iframeClicked.value = true;
      closePopover();
    }
  }, 0);
});

const recenteZoekopdrachten = useSessionStorage('OMNISEARCH_RECENTE_OPDRACHTEN', [] as string[]);

watch(term, (t) => {
  if (t && t.length >= 3) recenteZoekopdrachten.value.unshift(t);
  if (recenteZoekopdrachten.value.length > 10) recenteZoekopdrachten.value.pop();
});

const { data, error, isLoading } = useSearchGet(params, {
  query: {
    select: (e) => groupBy(e.result ?? [], (item) => item.type),
    enabled: computed(() => !!term.value && term.value.length >= 3),
  },
});
useWatchErrorHandler(error);

function getStatus(input: string): VStatusTagProps['color'] {
  switch (input) {
    case 'Actief':
    case 'Afgehandeld':
    case 'Akkoord':
    case 'Lopend':
      return 'teal';
    case 'Prospect':
      return 'grey';
    case 'In behandeling':
    case 'Afw. reactie TP':
    case 'Afw. reactie maatsch.':
      return 'orange';
    case 'Beëindigd':
    case 'Geroyeerd':
    case 'Geschorst':
      return 'red';
    default:
      return 'blue';
  }
}

function getIcon(input: ResultType): FontAwesomeIconProps['icon'] {
  switch (input) {
    case 'Relatie':
      return 'far fa-user';
    case 'Schade':
    case 'Schademelding':
      return 'far fa-car-crash';
    case 'Offerte':
      return 'far fa-calculator';
    case 'Aanvraag':
      return 'far fa-file-signature';
    case 'Mutatie':
      return 'far fa-file-circle-check';
    case 'Maatwerk':
      return 'far fa-memo-circle-check';
    case 'Royement':
      return 'far fa-file-xmark';
    case 'Polis':
      return 'far fa-check-double';
    default:
      return 'far fa-file';
  }
}

const keys = useMagicKeys();
const target = useTemplateRef('target');

whenever(keys.ctrl_space, () => {
  // de querytool gebruikt ctrl+spatie voor autocomplete suggesties, dit is niet aan te passen
  if (route.name === 'QueryTool') return;
  target.value?.click();
});

useEventListener(window, 'message', (event) => {
  const { type } = event.data;
  if (type !== 'search-shortcut') return;
  target.value?.click();
});

function open(event: Event) {
  if (isVisible.value === true) {
    popoverRef.value?.hide();
    return;
  }
  popoverRef.value?.show(event);
}

watch(isVisible, (visible) => {
  if (!visible) {
    term.value = undefined;
  }
});
</script>

<template>
  <div
    ref="target"
    class="flex cursor-pointer items-center px-4 text-primary-900 no-underline lg:px-6"
    @click="(e) => open(e)"
  >
    <button
      title="Zoeken"
      class="flex cursor-pointer items-center gap-1 border-none bg-transparent text-sm text-inherit outline-none"
      type="button"
    >
      <FontAwesomeIcon size="lg" icon="far fa-search" />
    </button>

    <VPopover ref="popover" class="max-h-[50vh] overflow-auto">
      <div class="w-[100dvw] xl:w-[588px]">
        <div class="flex flex-col px-2">
          <div class="sticky top-0 bg-white p-2">
            <VSearch
              v-model="term"
              placeholder="Zoeken op relatie-, polis-, schade-, offerte-, KVK-nummer of kenteken"
              class="overflow-hidden rounded-2xl transition-all duration-150 ease-in-out"
            />
          </div>
          <ExpandTransition group>
            <div v-for="group in data" :key="group.key" class="p-2">
              <span class="ml-2 flex items-center gap-1 text-sm text-grey-500">
                <FontAwesomeIcon :icon="getIcon(group.key as ResultType)" />
                {{ group.key }}
              </span>
              <div
                v-for="result in group.items"
                :key="result.omschrijving"
                class="group mt-2 rounded p-2 hover:bg-grey-100"
              >
                <a
                  :href="result.url"
                  class="flex flex-row items-center justify-between gap-2 text-grey-700"
                >
                  <div class="flex items-center gap-4">
                    <span :title="result.omschrijving" class="line-clamp-1 break-all font-bold">
                      {{ result.omschrijving }}
                    </span>
                  </div>
                  <div class="flex items-center">
                    <VStatusTag
                      inverted
                      class="text-nowrap bg-white text-xs"
                      :color="getStatus(result.status)"
                    >
                      {{ result.status }}
                    </VStatusTag>
                  </div>
                </a>
              </div>
            </div>
          </ExpandTransition>
          <div class="p-2" v-if="isLoading">
            <div class="mb-2 w-12">
              <VSkeleton :count="1" height="md" :timeout="0" v-if="isLoading" />
            </div>
            <VSkeleton :count="3" height="lg" :timeout="0" />
          </div>
          <div
            class="ml-2 flex items-center gap-1 pb-2 text-sm text-grey-500"
            v-if="Object.keys(data || {}).length === 0 && term?.length && !isLoading"
          >
            Er zijn geen resultaten gevonden. Probeer een andere zoekterm.
          </div>
          <div
            class="py-2"
            v-else-if="
              Object.keys(data || {}).length === 0 && !term && recenteZoekopdrachten.length
            "
          >
            <span class="ml-2 flex items-center gap-1 text-sm text-grey-500"> Recent gezocht </span>
            <div
              @click="term = result"
              v-for="result in recenteZoekopdrachten"
              :key="result"
              class="mt-2 rounded p-2 hover:bg-grey-100"
            >
              <div
                class="flex flex-row items-center justify-between gap-2 text-grey-700 hover:cursor-pointer"
              >
                <div class="flex items-center gap-4">
                  <span class="line-clamp-1 break-all font-bold"> {{ result }} </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VPopover>
  </div>
</template>
